<template>
  <div class="requirements" v-if="showReqirements">
    <h3>{{ $t('bounties.requirementsCaption') }}</h3>
    <v-list dense>
      <requirement-line
        v-if="showBountyLimit"
        :fulfilled="remainingRedeems > 0"
      >
        {{ redeemLimitText }}
      </requirement-line>
      <requirement-line
        v-if="neededPoints"
        :fulfilled="neededPoints.fulfilled"
      >
        {{ neededPoints.subtitle }}
      </requirement-line>
      <requirement-line
        v-if="validDate"
        :fulfilled="validDate.fulfilled"
      >
        {{ validDate.subtitle }}
      </requirement-line>
      <requirement-line
        v-if="bountyAvailable"
        :fulfilled="bountyAvailable.fulfilled"
      >
        {{ bountyAvailable.subtitle }}
      </requirement-line>
      <requirement-line
        v-if="singleUse"
        :fulfilled="singleUse.fulfilled"
      >
        {{ singleUse.subtitle }}
      </requirement-line>
      <requirement-line
        v-if="requiredStatus"
        :fulfilled="requiredStatus.fulfilled"
      >
        {{ requiredStatus.subtitle }}
      </requirement-line>
    </v-list>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import {mapGetters} from 'vuex'
import RequirementLine from './RequirementLine'

export default {
  name: 'Requirements',
  props: {
    bounty: {
      type: Object,
      required: true,
    },
    shop: {
      type: Object,
      default: () => {},
    }
  },
  components: {
    RequirementLine,
  },
  data() {
    return {
      possibleRedeemLimitTimeRanges: [
        'day',
        'week',
        'month',
        'year',
        'always',
        'every_x_days',
      ],
    }
  },
  computed: {
    ...mapGetters(['customerRelationship']),
    crPoints() {
      return _.defaultTo(_.get(this.customerRelationship, 'points'), 0)
    },
    neededPoints() {
      if (!this.bounty.needed_points) {
        return
      }

      let subtitle = this.$tc(
        'bounties.neededPointsRequirementText',
        this.bounty.needed_points,
        {
          count: this.bounty.needed_points.toLocaleString(),
          crPoints: this.crPoints.toLocaleString(),
        }
      )

      if (this.shop) {
        subtitle = this.$tc(
          'bounties.neededShopPointsRequirementText',
          this.bounty.needed_points,
          {
            count: this.bounty.needed_points.toLocaleString(),
            crPoints: this.crPoints.toLocaleString(),
            shopName: this.shop.name,
          }
        )
      }
      return {
        subtitle: subtitle,
        fulfilled: this.bounty.needed_points <= this.bounty.cr_points
      }
    },
    validDate() {
      let fulfilled = false
      let subtitle = ''
      let validFrom = _.get(this.bounty, 'valid_from')
      let validUntil = _.get(this.bounty, 'valid_until')

      if (_.isNil(validFrom) && _.isNil(validUntil)) {
        return null
      }

      if (!_.isNil(validFrom)) {
        validFrom = moment(validFrom)
      }

      if (!_.isNil(validUntil)) {
        validUntil = moment(validUntil)
      }

      if (!_.isNil(validFrom) && !_.isNil(validUntil)) {
        fulfilled = moment().isBetween(validFrom, validUntil, null, [])
        subtitle = this.$t(
          'bounties.validDatesFromUntilRequirementText',
          {
            validFrom: validFrom.format('DD.MM.YYYY HH:mm'),
            validUntil: validUntil.format('DD.MM.YYYY HH:mm'),
          }
        )
      } else if (!_.isNil(validFrom)) {
        fulfilled = moment().isSameOrAfter(validFrom)
        subtitle = this.$t(
          'bounties.validDatesFromRequirementText',
          {
            validFrom: validFrom.format('DD.MM.YYYY HH:mm'),
          }
        )
      } else if (!_.isNil(validUntil)) {
        fulfilled = moment().isSameOrBefore(validUntil)
        subtitle = this.$t(
          'bounties.validDatesUntilRequirementText',
          {
            validUntil: validUntil.format('DD.MM.YYYY HH:mm'),
          }
        )
      }

      return {subtitle, fulfilled}
    },
    bountyAvailable() {
      if (!this.bounty.limited) {
        return null
      }

      const subtitle = this.$t(
        'bounties.bountyAvailabilityRequirementText',
        {
          amount: this.bounty.amount,
          count: this.bounty.availability,
        }
      )

      return {
        subtitle: subtitle,
        fulfilled: this.bounty.availability > 0,
      }
    },
    singleUse() {
      if (!this.bounty.single_use || _.isNil(this.bounty.redeem_count)) {
        return null
      }

      const subtitle = this.$tc(
        'bounties.singleUseRequirementText',
        this.bounty.redeem_count
      )
      return {
        subtitle: subtitle,
        fulfilled: this.bounty.single_use && this.bounty.redeem_count === 0,
      }
    },
    requiredStatus() {
      const bountyRequiredStatus = _.get(this.bounty, 'required_status')
      const userStatus = _.get(this.customerRelationship, 'status')
      let fulfilled = false

      if (_.isNil(bountyRequiredStatus)) {
        return null
      }

      const subtitle = this.$t(
        'bounties.bountyStatusRequirementText',
        {
          statusName: _.get(bountyRequiredStatus, 'name', ''),
        }
      )

      userStatus.forEach((status) => {
        if (
          _.get(bountyRequiredStatus, 'name', '') === _.get(status, 'name', '')
            && !_.isEmpty(_.get(bountyRequiredStatus, 'name', ''))
            && !_.isEmpty(_.get(status, 'name', ''))
        ) {
          fulfilled = true
        }
      })

      return {subtitle, fulfilled}
    },
    redeemLimit() {
      return _.get(this.bounty, 'throttle.redeem_limit')
    },
    rangeRedeemCount() {
      return _.get(this.bounty, 'time_range_redeem_count')
    },
    redeemLimitTimeRange() {
      return _.get(this.bounty, 'throttle.redeem_limit_time_range')
    },
    redeemWaitTime() {
      const waitTime = _.get(this.bounty, 'throttle.wait_time')

      if (waitTime) {
        return moment.duration(waitTime).days()
      }

      return ''
    },
    remainingRedeems() {
      return this.redeemLimit - this.rangeRedeemCount
    },
    showBountyLimit() {
      const isValidRedeemTimeRange =
        this.possibleRedeemLimitTimeRanges.includes(this.redeemLimitTimeRange)

      return _.isInteger(this.redeemLimit)
        && _.isInteger(this.rangeRedeemCount)
        && isValidRedeemTimeRange
    },
    redeemLimitText() {
      let redeemLimitTimeRangeText = ''
      let waitInDays = ''

      if (this.redeemLimitTimeRange === 'every_x_days') {
        redeemLimitTimeRangeText = this.$tc(
          'bounties.bountyRedeemLimit.timeRange.every_x_days',
          this.redeemLimit,
        )

        waitInDays = this.$tc(
          'bounties.bountyRedeemLimit.waitInDays',
          this.redeemWaitTime
        )
      } else {
        redeemLimitTimeRangeText = this.$t(
          `bounties.bountyRedeemLimit.timeRange.${this.redeemLimitTimeRange}`
        )
      }

      let limitTranslation

      if (this.remainingRedeems === 0) {
        limitTranslation =
          'bounties.bountyRedeemLimit.limitText.notAvailable'
      } else if (this.redeemLimitTimeRange === 'always') {
        limitTranslation =
          'bounties.bountyRedeemLimit.limitText.alwaysAvailable'
      } else if (this.redeemLimitTimeRange === 'every_x_days') {
        limitTranslation =
          'bounties.bountyRedeemLimit.limitText.xDaysAvailable'
      } else {
        limitTranslation =
          'bounties.bountyRedeemLimit.limitText.restrictedAvailable'
      }

      return this.$t(limitTranslation, {
        redeemLimit: this.redeemLimit,
        redeemCount: this.rangeRedeemCount,
        redeemLimitTimeRangeText,
        redeemsRemaining: this.remainingRedeems,
        redeemWaitTime: this.redeemWaitTime,
        waitInDays,
      })
    },
    showReqirements() {
      return !!this.neededPoints
        || !!this.validDate
        || !!this.bountyAvailable
        || !!this.singleUse
        || !!this.requiredStatus
    },
  },
}
</script>

<style lang="scss" scoped></style>
